import { useDeleteAPI, useGetAPI, usePatchAPI, usePostAPI } from './useAPI';
import {
	AddressSearchResponse,
	PostcodeParseResponse,
	ParseAddressResponse,
	AppEnums,
	TLocation,
	TLocationContact,
	TLocationShipperDetail,
	AJAXError,
	TDocument,
	TIssueAndClaimDocument,
	TRateCardSettings,
	ILane,
	IQuote,
	TNotificationConfigs,
	APICallProps,
	TSavedAddress,
	RateCardHistory,
	TLinks,
	TMeta,
	PowerBIConfig,
	SaveRateCardSettingsAPICallProps,
	TCompleteQuotePayloadAPICallProps,
	IRateCard_Depricated,
	TPermissionConfigs,
	TShiperETAReason,
	TShipperETAResponse,
	TETAValidateResponse,
	TDataGridResponse,
	TRateCardItemDefinition,
	TFilterObj,
	ABMTeam,
	TSurcharge,
	TLinkedRateCard,
} from '@types';
import ApiClient from './axiosSetup';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { SID, CID, XCustomerID } from '@state';
import { normaliseFuelLevies } from '@utils';

/*	XSRF priming. Uses fetch with redirect: 'manual' so 302s with absolute Location
	headers (EOS does this for unrecognised host patterns like *.features.dev.env.ofload.com)
	don't cross-navigate the browser. Set-Cookie on the 302 still applies, which is
	all we need. axios can't do this in the browser — XHR always follows redirects. */
export const useGetXSRF = () => {
	const callGetXSRF = async () => {
		try {
			await fetch('/inertia/login', {
				method: 'GET',
				redirect: 'manual',
				credentials: 'same-origin',
			});
		} catch {
			/* best-effort priming; swallow network errors */
		}
	};

	return { callGetXSRF };
}

/*  ENUM Fetch */

export const useGetEnums = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/enum',
		domain: 'AdminAPI'
	});

	return {
		isLoading,
		enums: data as { data: AppEnums[] },
		error: error as AJAXError,
		callGetEnums: callAPI
	}
}

export const useGetMachshipSurcharges = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/machship/consignment-creation-settings',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		isLoading,
		data: data as { data: TSurcharge[] },
		error: error as AJAXError,
		callGetMachshipSurcharges: callAPI
	}
}

export const useGetEOSEnums = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/v2/super-dock/enums', });

	return {
		isEosEnumLoading: isLoading,
		eosEnums: data,
		eosEnumError: error as AJAXError,
		callGetEOSEnums: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

/* DataGrid Fetch Hook */
export const useGetDataGridData = <TData>(fetchURL: string, id?: string) => {
	const headers = id ? { 'X-Shipper-Token': `${id}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: fetchURL, headers });

	return {
		isLoadingData: isLoading,
		data: data as TDataGridResponse<TData>,
		dataErrors: error,
		callGetData: callAPI as <TData>(props?: APICallProps) => Promise<{
			data: TDataGridResponse<TData>;
			error: AJAXError | null
		}>
	}
}

/**
 * Fetch current user(me) data
 */
export const useGetMe = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: `/api/me` });

	return {
		isLoadingMe: isLoading,
		me: data,
		meError: error as AJAXError,
		callGetMe: callAPI
	}
}

/*  Fetch User/Team Data */

export const useGetTeam = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: `/api/team` });

	return {
		isLoadingTeam: isLoading,
		team: data,
		teamError: error as AJAXError,
		callGetTeam: callAPI
	}
}


export const useGetTeamABMList = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: `/api/admin/team/abm-list` });

	return {
		isLoadingTeam: isLoading,
		team: data as ABMTeam[],
		teamError: error as AJAXError,
		callGetTeam: callAPI as (props?: APICallProps) => Promise<{
			data: ABMTeam[];
			error: AJAXError | null
		}>
	}
}

export const useGetGeneralNotificationConfig = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/notification-configurations/{1}' });

	return {
		loadingGeneralNotificationConfig: isLoading,
		generalNotificationConfig: data as { data: TNotificationConfigs },
		getGeneralNotificationConfigError: error as AJAXError,
		clearGeneralNotificationConfigData: clearData,
		clearGeneralNotificationConfigError: clearError,
		callGetGeneralNotificationConfig: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TNotificationConfigs } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetGeneralPermissionConfig = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/permission-configurations' });

	return {
		loadingGeneralPermissionConfig: isLoading,
		generalPermissionConfig: data as { data: TPermissionConfigs },
		getGeneralPermissionConfigError: error as AJAXError,
		clearGeneralPermissionConfigData: clearData,
		clearGeneralPermissionConfigError: clearError,
		callGetGeneralPermissionConfig: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TPermissionConfigs } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetTeamNotificationConfig = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/team/{1}/notification-configurations' });

	return {
		loadingTeamNotificationConfig: isLoading,
		teamNotificationConfig: data as { data: TNotificationConfigs },
		getTeamNotificationConfigError: error as AJAXError,
		clearGetTeamNotificationConfigData: clearData,
		clearGetTeamNotificationConfigError: clearError,
		callGetTeamNotificationConfig: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TNotificationConfigs } | null;
			error: AJAXError | null
		}>
	}
}

export const useGetTeamPermissionConfig = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/team/{1}/permission-configurations' });

	return {
		loadingTeamPermissionConfig: isLoading,
		teamPermissionConfig: data as { data: TPermissionConfigs },
		getTeamPermissionConfigError: error as AJAXError,
		clearGetTeamPermissionConfigData: clearData,
		clearGetTeamPermissionConfigError: clearError,
		callGetTeamPermissionConfig: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TPermissionConfigs } | null;
			error: AJAXError | null
		}>
	}
}

export const useUpdateTeamMember = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/team-update/{1}' });

	return {
		loadingUpdateTeamMember: isLoading,
		updateTeamMember: data as { type: string; message: string },
		updateTeamMemberError: error as AJAXError,
		clearUpdateTeamMemberData: clearData,
		clearUpdateTeamMemberError: clearError,
		callUpdateTeamMember: callAPI as (props?: APICallProps) => Promise<{
            data: { type: string; message: string } | null;
            error: AJAXError | null
        }>
	}
}

export const useCreateTeamMember = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/team-create/{1}' });

	return {
		loadingCreateTeamMember: isLoading,
		createTeamMember: data as { type: string; message: string },
		createTeamMemberError: error as AJAXError,
		clearCreateTeamMemberData: clearData,
		clearCreateTeamMemberError: clearError,
		callCreateTeamMember: callAPI as (props?: APICallProps) => Promise<{
            data: { type: string; message: string } | null;
            error: AJAXError | null
        }>
	}
}

export const useDeleteTeamMember = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/team-delete/{1}' });

	return {
		loadingDeleteTeamMember: isLoading,
		deleteTeamMember: data as { type: string; message: string },
		deleteTeamMemberError: error as AJAXError,
		clearDeleteTeamMemberData: clearData,
		clearDeleteTeamMemberError: clearError,
		callDeleteTeamMember: callAPI as (props?: APICallProps) => Promise<{
            data: { type: string; message: string } | null;
            error: AJAXError | null
        }>
	}
}


export const useGetAutocomplete = (regionsForCustomer: string|null = null) => {
	const headers = regionsForCustomer ? { 'X-Customer-Id': `${regionsForCustomer}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/autocomplete{1}',
		domain: 'ShipmentAPI',
		headers
	});
	const triggerAPI = (term: string, locality_type?: string) => {
		let params = `?term=${term}`;
		if (locality_type) {
			/* 	Currently the AddressPicker component only allows for one locality type at a time (string)
			   	will need to come back and update if its required to send 2 types,
			   	(To send all 3, simply don't include the locality_type in the first place!) */
			params += `&${encodeURIComponent('locality_type[]')}=${locality_type}`;
		}
		// Error is surfaced via the hook's error state; catch so a failed lookup never becomes an unhandled rejection
		callAPI({ routes: [params] }).catch(() => undefined);
	}
	return {
		loadingAutocomplete: isLoading,
		addresses: data as AddressSearchResponse,
		getAutocompleteError: error as AJAXError,
		callGetAutocomplete: triggerAPI
	}
}

export const useGetSavedAddresses = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/locations/address/locations', headers: headers });

	return {
		loadingSavedAddresses: isLoading,
		savedAddresses: data as TSavedAddress[],
		getSavedAddressesError: error as AJAXError,
		clearSavedAddressesError: clearError,
		callGetSavedAddresses: callAPI
	}
}

export const useGetParseAddress = () => {
	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/address-api/address/parse' });

	return {
		loadingParseAddress: isLoading,
		parsedAddress: data as ParseAddressResponse,
		getParseAddressError: error as AJAXError,
		clearParseAddress: clearData,
		clearParseAddressError: clearError,
		callGetParseAddress: callAPI as (props?: APICallProps) => Promise<{
            data: ParseAddressResponse | null;
            error: AJAXError | null
        }>
	}
}

export const useGetParsePostcode = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/address-api/postcode/parse' });

	return {
		loadingParsePostcode: isLoading,
		parsedPostcode: data as PostcodeParseResponse,
		getParsePostcodeError: error as AJAXError,
		callGetParsePostcode: callAPI as (props?: APICallProps) => Promise<{
            data: PostcodeParseResponse | null;
            error: AJAXError | null
        }>
	}
}

export const useGetAdminLocations = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/locations' });

	return {
		loadingLocation: isLoading,
		location: data as {
			rows: TLocation[];
			perPage: number;
			totalRows: number;
			currentPage: number;
		},
		locationError: error,
		clearLocationError: clearError,
		callGetLocation: callAPI
	}
}
export const useGetLocations = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/locations', headers: headers, avoidCache: true
	});

	return {
		loadingLocation: isLoading,
		location: data as {
			rows: TLocation[];
			perPage: number;
			totalRows: number;
			currentPage: number;
		},
		locationError: error,
		clearLocationError: clearError,
		callGetLocation: callAPI
	}
}

export const useGetLocation = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/locations/{1}', headers });

	return {
		loadingLocation: isLoading,
		location: data as TLocation,
		locationError: error,
		clearLocationError: clearError,
		callGetLocation: callAPI
	}
}

export const useGetAdminLocation = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/locations/{1}' });

	return {
		loadingLocation: isLoading,
		location: data as TLocation,
		locationError: error,
		clearLocationError: clearError,
		callGetLocation: callAPI
	}
}

export const useUpdateAdminLocation = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/locations/{1}' });

	return {
		loadingUpdateLocation: isLoading,
		updateLocation: data as TLocation,
		updateLocationError: error as AJAXError,
		clearUpdateLocationError: clearError,
		callUpdateLocation: callAPI
	}
}

export const useCreateAdminLocation = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/locations' });

	return {
		loadingCreateLocation: isLoading,
		createLocation: data as TLocation,
		createLocationError: error as AJAXError,
		clearCreateLocationError: clearError,
		callCreateLocation: callAPI
	}
}

export const useGetLocationContacts = (id: string) => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper/locations/{1}/contacts', headers: headers });

	const callGetContacts = () => {
		callAPI({ routes: [id] });
	};

	return {
		loading: isLoading,
		contacts: data as {data: TLocationContact[]},
		error: error,
		clearError: clearError,
		callGetContacts: callGetContacts
	}
}

export const useGetAdminLocationContacts = (id: string) => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/locations/{1}/contacts', });

	const callGetContacts = () => {
		callAPI({ routes: [id] });
	};

	return {
		loading: isLoading,
		contacts: data as { data: TLocationContact[] },
		error: error,
		clearError: clearError,
		callGetContacts: callGetContacts
	}
}

export const useDeleteLocationContact = (locationId: string) => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		error,
		data,
		clearError,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/shipper/locations/{1}/contacts/{2}', headers });

	const deleteContact = async (contact: TLocationContact) => {
		await callAPI({ routes: [locationId, String(contact.id!)] });
	};

	return {
		deleting: isLoading,
		error: error,
		data,
		clearError: clearError,
		deleteContact
	}
}

export const useDeleteAdminLocationContact = (locationId: string) => {
	const {
		isLoading,
		error,
		data,
		clearError,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/locations/{1}/contacts/{2}' });

	const deleteContact = async (contact: TLocationContact) => {
		await callAPI({ routes: [locationId, String(contact.id!)] });
	};

	return {
		deleting: isLoading,
		error: error,
		data,
		clearError: clearError,
		deleteContact
	}
}

export const useDeleteShipperDetailContact = () => {
	const {
		isLoading,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/locations/{1}/shipper-details/{2}/contacts/{3}' });

	return {
		deleting: isLoading,
		callDeleteShipperDetailContact: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TLocationContact };
			error: AJAXError | null;
		}>
	}
}

export const useDeleteShipperDetail = () => {
	const {
		isLoading,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/locations/{1}/shipper-details/{2}' });

	return {
		deleting: isLoading,
		callDeleteShipperDetail: callAPI as (props?: APICallProps) => Promise<{
			data: { message: string };
			error: AJAXError | null;
		}>
	}
}

export const useUpdateShipperDetail = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/locations/{1}/shipper-details/{2}' });

	return {
		updating: isLoading,
		callUpdateShipperDetail: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TLocationShipperDetail };
			error: AJAXError | null;
		}>
	}
}

export const useCreateShipperDetail = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/locations/{1}/shipper-details' });

	return {
		creating: isLoading,
		callCreateShipperDetail: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TLocationShipperDetail };
			error: AJAXError | null;
		}>
	}
}

export const usePostShipperDetailContact = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/locations/{1}/shipper-details/{2}/contacts' });

	return {
		saving: isLoading,
		callPostShipperDetailContact: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TLocationContact };
			error: AJAXError | null;
		}>
	}
}

export const usePostLocationContacts = (id: string) => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/shipper/locations/{1}/contacts', headers });

	const saveContact = async (contacts: TLocationContact) => {
		await callAPI({
			routes: [id],
			payload: { form: contacts }
		});
	}

	return {
		loading: isLoading,
		contacts: data as TLocationContact[],
		error: error,
		clearError: clearError,
		postContacts: saveContact
	}
}

export const usePostAdminLocationContacts = (id: string) => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/admin/locations/{1}/contacts' });

	const saveContact = async (contacts: TLocationContact) => {
		await callAPI({
			routes: [id],
			payload: { form: contacts }
		});
	}

	return {
		loading: isLoading,
		contacts: data as TLocationContact[],
		error: error,
		clearError: clearError,
		postContacts: saveContact
	}
}


export const useGetAdminLocationConnectedShippers = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/admin/locations/{1}/shipper-details' });
	return {
		loading: isLoading,
		shipperDetails: data?.data as TLocationShipperDetail[] ?? [],
		error: error,
		clearError: clearError,
		callGetConnectedShippers: callAPI
	}
}

export const usePostAdminLocationConnectedShippers = () => {
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI(
		{ endpoint: '/api/admin/locations/{1}/shippers' });
	return {
		loading: isLoading,
		connectedShippers: data,
		error: error,
		clearError: clearError,
		postConnectedShippers: callAPI
	}
}

export const usePostLocationChangeRequest = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/locations/{1}/request-change',
		headers
	});

	const triggerAPI = async (id: string, payload: any) => {
		await callAPI({ routes: [id], payload });
	}

	return {
		loadingChangeRequest: isLoading,
		changeRequestData: data as {
			rows: TLocation[];
			perPage: number;
			totalRows: number;
			currentPage: number;
		},
		changeRequestError: error,
		clearChangeRequestError: clearError,
		callPostChangeRequest: triggerAPI
	}
}

export const useDeleteAdminLocation = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/admin/locations/{1}' });

	return {
		loadingDeleteLocation: isLoading,
		deletedLocation: data as {
			external_id: string;
		},
		deleteLocationError: error as AJAXError,
		callDeleteLocation: callAPI
	}
}


export const useGetDocument = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/documents/{1}?format={2}' });

	const triggerAPI = (id: number, format: string) => {
		callAPI({ routes: [`${id}`, `${format}`] });
	}

	const downloadDocument = (id: number, format: string) => {
		return ApiClient.get(`/api/documents/${id}?format=${format}`)
			.then((res) => res.data as string);
	}

	return {
		loadingGetDocument: isLoading,
		document: data as string,
		getDocumentError: error,
		callGetDocument: triggerAPI,
		// avoid breaking changes when updating callGetDocument
		downloadDocument
	}
}

export const useDownloadMasterConsignmentNote = () => {
	const sID = useRecoilValue(SID);
	const {
		isLoading,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/consignment-note/download-multi',
		headers: sID ? { 'X-Shipper-Token': `${sID}` } : {}
	});

	const downloadMasterConsignmentNote = (masterReference: string) => {
		return callAPI({
			payload: { master_reference: masterReference },
			responseType: 'blob'
		}).then((res) => res?.data as Blob);
	}

	return {
		loadingMasterConsignmentNote: isLoading,
		downloadMasterConsignmentNote
	}
}

export const useGetMultipleDocuments = () => {
	const {
		isLoading,
		error,
	} = useGetAPI({ endpoint: '/api/documents/{1}?format={2}' });
	const [retData, setRetData] = useState<any | null>(null);

	const downloadDocument = async (id: number, format: string) => {
		return await ApiClient.get(`/api/documents/${id}?format=${format}`)
			.then((res) => {
				return res.data as string
			});
	}

	const triggerAPI = async (documents: TIssueAndClaimDocument[], format: string) => {
		const documentPromises = documents?.map(async (document) => {
			const documentUri = await downloadDocument(document.doc_id, format);
			return {
				uri: documentUri,
				fileType: document.document.object_type,
			};
		}) ?? [];

		const docsToSend = await Promise.all(documentPromises);
		setRetData(docsToSend);

	}
	const getData = () => {
		if (!retData) return null;
		return retData as any;
	}
	return {
		loadingGetDocuments: isLoading,
		documents: getData(),
		getDocumentsError: error,
		callGetDocuments: triggerAPI
	}
}

export const usePostDocument = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/documents',
		headers: { 'Content-Type': 'multipart/form-data', },
	});

	const triggerAPI = (payload: FormData) => {
		callAPI({ payload });
	}

	return {
		loadingPostDocument: isLoading,
		postDocumentData: data as {data: TDocument},
		getPostDocumentError: error,
		callPostDocument: triggerAPI
	}
}

export const useGetRateCardGlobalSettings = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/rate-card-setting/global/{1}`,
		domain: 'ShipmentAPI',
		headers
	});

	return {
		isLoadingRateCardGlobalSettings: isLoading,
		rateCardGlobalSettings: data as TRateCardSettings,
		getErrorRateCardGlobalSettings: error as AJAXError,
		callGetRateCardGlobalSettings: callAPI as (props?: APICallProps) => Promise<{
			data: TRateCardSettings | null;
			error: AJAXError | null
		}>
	}
}

export const useGetRateCardSettings = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/rate-card-setting/{1}`,
		domain: 'ShipmentAPI',
		headers
	});

	const call = callAPI as (props?: APICallProps) => Promise<{
		data: TRateCardSettings | null;
		error: AJAXError | null
	}>;

	const callGetRateCardSettings = (props?: APICallProps) => call(props).then((response) => {
		if (!response?.data) return response;
		return {
			...response,
			data: { ...response.data, fuel_levies: normaliseFuelLevies(response.data.fuel_levies) }
		};
	});

	return {
		isLoadingRateCardSettings: isLoading,
		rateCardSettings: data as TRateCardSettings,
		getErrorRateCardSettings: error as AJAXError,
		callGetRateCardSettings
	}
}

export const useSaveRateCardSettings = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: `/api/rate-card-setting/{1}`,
		domain: 'ShipmentAPI',
		headers
	});

	return {
		isLoadingSaveRateCardSettings: isLoading,
		saveRateCardSettings: data as any,
		getErrorSaveRateCardSettings: error as AJAXError,
		callSaveRateCardSettings: callAPI as (props?: SaveRateCardSettingsAPICallProps) => Promise<{
			data: {
				data: TRateCardSettings
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useGetRateCard = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/ratecard/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		isLoadingGetRateCard: isLoading,
		rateCard: data?.data as IRateCard_Depricated,
		getGetRateCardError: error,
		callGetRateCard: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: IRateCard_Depricated
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useGetLanes = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		clearData,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/lanes/{1}/{2}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingLanes: isLoading,
		lanes: data as { data: ILane[], links: TLinks, meta: TMeta },
		getLanesError: error as AJAXError,
		clearGetLanesError: clearError,
		clearGetLanesData: clearData,
		callGetLanes: callAPI as (props?: APICallProps) => Promise<{
            data: { data: ILane[], links: TLinks, meta: TMeta } | null;
            error: AJAXError | null
        }>
	}
}

export const useGetLane = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/lanes/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	const call = callAPI as (props?: APICallProps) => Promise<{
		data: { data: ILane } | null;
		error: AJAXError | null
	}>;

	const callGetLane = (props?: APICallProps) => call(props).then((response) => {
		if (!response?.data?.data) return response;
		return {
			...response,
			data: {
				...response.data,
				data: { ...response.data.data, fuel_levies: normaliseFuelLevies(response.data.data.fuel_levies) }
			}
		};
	});

	return {
		isLoadingGetLane: isLoading,
		getLane: data?.data as ILane,
		getGetLaneError: error,
		callGetLane
	}
}

export const useGetLinkedLanes = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		callAPI
	} = useGetAPI({
		endpoint: '/api/lanes/linked/list',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		loadingLinkedLanes: isLoading,
		linkedLanes: data as { data: ILane[], links: TLinks, meta: TMeta },
		callGetLinkedLanes: callAPI as (props?: APICallProps) => Promise<{
			data: { data: ILane[], links: TLinks, meta: TMeta } | null;
			error: AJAXError | null
		}>
	}
}


export const useGetLinkedLane = () => {
	const {
		isLoading,
		data,
		clearData,
		error,
		clearError,
		callAPI
	} = useGetAPI({
		endpoint: '/api/lanes/linked',
		domain: 'ShipmentAPI',
	});

	return {
		isLoadingGetLinkedLane: isLoading,
		getLinkedLane: data as { data: ILane, links: TLinks, meta: TMeta },
		getLinkedLaneError: error as AJAXError,
		clearGetLinkedLaneError: clearError,
		clearGetLinkedLaneData: clearData,
		callGetLinkedLane: callAPI as (props?: APICallProps) => Promise<{
            data: { data: ILane, links: TLinks, meta: TMeta } | null;
            error: AJAXError | null
        }>
	}
}

export const useCreateLinkedLane = () => {
	const {
		isLoading,
		data,
		callAPI
	} = usePostAPI({
		endpoint: '/api/lanes/linked',
		domain: 'ShipmentAPI',
	});

	return {
		loadingCreateLinkedLane: isLoading,
		CreateLinkedLane: data,
		callCreateLinkedLane: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: ILane
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useUpdateLinkedLane = () => {
	const {
		isLoading,
		data,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/lanes/linked/{1}',
		domain: 'ShipmentAPI',
	});

	return {
		loadingUpdateLinkedLane: isLoading,
		updateLinkedLane: data,
		callUpdateLinkedLane: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: ILane
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useGetLinkedLaneConfiguration= () => {
	const {
		isLoading,
		data,
		callAPI
	} = useGetAPI({
		endpoint: '/api/lanes/linked/{1}/configuration',
		domain: 'ShipmentAPI',
	});

	return {
		loadingGetLinkedLaneConfiguration: isLoading,
		linkedLaneConfiguration: data as TLinkedRateCard,
		callGetLinkedLaneConfiguration: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TLinkedRateCard,
			} | null;
			error: AJAXError | null
		}>
	}
}

export const usePostPreviewCustomerLaneWithMargin= () => {
	const {
		isLoading,
		data,
		callAPI
	} = usePostAPI({
		endpoint: '/api/lanes/customer/{1}/apply-margin',
		domain: 'ShipmentAPI',
	});

	return {
		loadingPreviewCustomerLaneWithMargin: isLoading,
		previewCustomerLane: data as { data: ILane },
		callPostPreviewCustomerLaneWithMargin: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: ILane
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useLaneRateCardsHistory = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/lanes/{1}/history',
		domain: 'ShipmentAPI'
	});

	return {
		isLoadingGetLane: isLoading,
		getHistory: data?.data,
		getGetHistoryError: error,
		callGetHistory: callAPI as (props?: APICallProps) => Promise<{
			data: RateCardHistory | null;
			error: AJAXError | null
		}>
	}
}

export const useGetQuote = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper/quote/requested/quote/{1}',
		domain: 'EOS',
		headers
	});

	return {
		isLoadingQuote: isLoading,
		quote: data?.data as IQuote,
		getQuoteError: error,
		callGetQuote: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: IQuote
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useSaveLane = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/lanes/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		isLoadingSaveLane: isLoading,
		saveLaneData: data?.data as ILane,
		getSaveLaneError: error,
		callSaveLane: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: ILane
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useUpdateLane = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/lanes/{1}',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		isLoadingUpdateLane: isLoading,
		updateLaneData: data?.data as ILane,
		getUpdateLaneError: error,
		callUpdateLane: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: ILane
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useCompleteQuote = () => {
	const sID = useRecoilValue(SID);
	const headers = sID ? { 'X-Shipper-Token': `${sID}` } : undefined;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/shipper/quote/response/{1}',
		headers
	});

	return {
		isLoadingCompleteQuote: isLoading,
		completeQuoteData: data?.data,
		getCompleteQuoteError: error,
		callCompleteQuote: callAPI as (props?: TCompleteQuotePayloadAPICallProps) => Promise<{
			data: {
				data: string;
			} | null;
			error: AJAXError | null
		}>
	}
}

export const useGetShipmentTags = () => {
	const {
		data,
		error,
		isLoading,
		callAPI
	} = useGetAPI({
		endpoint: '/api/shipper-tag',
		domain: 'ShipmentAPI'
	});

	return {
		isLoadingTags: isLoading,
		tagsList: data?.data as {
			name: string;
			external_id: string;
		}[],
		tagsError: error,
		callGetShipmentTags: callAPI,
	}
}

export const useGetShipperCustomItems = () => {
	const customerID = useRecoilValue(XCustomerID);
	const headers = customerID ? { 'X-Customer-Id': `${customerID}` } : undefined;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/custom-items',
		domain: 'ShipmentAPI',
		headers
	});

	return {
		isLoadingCustomItems: isLoading,
		customItems: data?.data as TRateCardItemDefinition[],
		getCustomItemsError: error,
		callCustomItems: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TRateCardItemDefinition[]
			} | null;
			error: AJAXError | null
		}>
	}
}

/* 	Analytics */
export const useGetPowerBiReport = (customer: 'shipper' | 'carrier') => {
	const sID = useRecoilValue(SID);
	const cID = useRecoilValue(CID)
	const headers: { [key: string]: string } | undefined = (customer === 'shipper' && sID) ? { 'X-Shipper-Token': String(sID) }
		: (customer === 'carrier' && cID) ? { 'X-Carrier-Token': String(cID) }
			: undefined;

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: `/api/${customer}/power-bi/report/{1}`, headers });

	return {
		loadingPowerBi: isLoading,
		powerBi: data as PowerBIConfig,
		getPowerBiError: error as AJAXError,
		clearPowerBiError: clearError,
		clearPowerBiData: clearData,
		callGetPowerBi: callAPI as (props?: APICallProps) => Promise<{
			data: PowerBIConfig | null;
			error: AJAXError | null
		}>
	}
}


export const useGetShipperETAReasons = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({ endpoint: '/api/shipper-eta-reasons', });

	return {
		isLoadingETAReasons: isLoading,
		ETAReasons: data as { data: TShiperETAReason[] },
		ETAReasonsErrors: error,
		callGetETAReasons: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShiperETAReason[] };
			error: AJAXError | null
		}>
	}
}

export const usePostETARequest = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/estimation-time-of-arrival', });

	return {
		isLoadingPostETA: isLoading,
		PostETA: data as { data: TShipperETAResponse },
		PostETAErrors: error,
		callPostETA: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipperETAResponse };
			error: AJAXError | null
		}>
	}
}

export const useApproveETA = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/eta/{1}/approve' });

	return {
		isLoadingApproveETA: isLoading,
		callApproveETA: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipperETAResponse };
			error: AJAXError | null
		}>
	}
}

export const useRejectETA = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/admin/eta/{1}/reject' });

	return {
		isLoadingRejectETA: isLoading,
		callRejectETA: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TShipperETAResponse };
			error: AJAXError | null
		}>
	}
}

export const useValidateETARequest = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/estimation-time-of-arrival/validate' });

	return {
		isLoadingValidateETA: isLoading,
		validateETAData: data as { data: TETAValidateResponse },
		validateETAError: error as AJAXError,
		callValidateETA: callAPI as (props?: APICallProps) => Promise<{
			data: { data: TETAValidateResponse };
			error: AJAXError | null
		}>
	}
}
// Shipper Details Document APIs
export const useDeleteShipperDetailDocument = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({ endpoint: `/api/admin/locations/{1}/shipper-details/{2}/documents/{3}` });

	const deleteDocument = async (locationId: string, shipperDetailId: number, docId: number|string) => {
		await callAPI({ routes: [locationId, shipperDetailId.toString(), docId.toString()] });
	};

	return {
		deleting: isLoading,
		deleteData: data,
		deleteError: error,
		callDeleteDocument: deleteDocument
	};
};

export const useLinkShipperDetailDocument = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: `/api/admin/locations/{1}/shipper-details/{2}/documents` });

	const linkDocument = async (locationId: string, shipperDetailId: number, docId: number) => {
		await callAPI({
			routes: [locationId, shipperDetailId.toString()],
			payload: { doc_id: docId }
		});
	};

	return {
		linking: isLoading,
		linkData: data,
		linkError: error,
		callLinkDocument: linkDocument
	};
};

export const useGetDataGridFilters = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/user-filter' });

	return {
		isLoadingGetFilters: isLoading,
		callGetDataGridFilters: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: {
					name: string;
					external_id: string;
					filter: {
						slug: string;
						body: TFilterObj[]
					}
				}[];
			}
			error: AJAXError | null;
		}>
	}
}

export const useGetDataGridFilter = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/user-filter/{1}' });

	return {
		isLoadingGetFilter: isLoading,
		callGetDataGridFilter: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: {
					name: string;
					external_id: string;
					filter: {
						slug: string;
						body: TFilterObj[]
					}
				}
			};
			error: AJAXError | null;
		}>
	}
}

export const useCreateDataGridFilter = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/user-filter' });

	return {
		isLoadingCreateFilter: isLoading,
		callCreateDataGridFilter: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: {
					external_id: string;
					name: string;
					filter: {
						slug: string;
						body: TFilterObj[];
					}
				};
			}
			error: AJAXError | null;
		}>
	}
}

export const useUpdateDataGridFilter = () => {
	const {
		isLoading,
		callAPI
	} = usePatchAPI({ endpoint: '/api/user-filter/{1}' });

	return {
		isLoadingUpdateFilter: isLoading,
		callUpdateDataGridFilter: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	}
}

export const useDeleteDataGridFilter = () => {
	const {
		isLoading,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/user-filter/{1}' });

	return {
		isLoadingDeleteFilter: isLoading,
		callDeleteDataGridFilter: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	}
}

export const useCreateSharedFilter = () => {
	const {
		isLoading,
		callAPI
	} = usePostAPI({ endpoint: '/api/shared-filter' });

	return {
		isLoadingCreateSharedFilter: isLoading,
		callCreateSharedFilter: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: {
					shared_filter_identifier: string;
					expires_at_epoch_seconds: number;
				};
			}
			error: AJAXError | null;
		}>
	}
}

export const useGetSharedFilter = () => {
	const {
		isLoading,
		callAPI
	} = useGetAPI({ endpoint: '/api/shared-filter/{1}' });

	return {
		isLoadingGetSharedFilter: isLoading,
		callGetSharedFilter: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: {
					filter: TFilterObj[];
					table_id: string;
				};
			}
			error: AJAXError | null;
		}>
	}
}
